import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, Copy, Share2, Users, Gift, CheckCircle, 
  Clock, TrendingUp, ExternalLink
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/referrals/stats`),
        axios.get(`${API}/referrals/list`)
      ]);
      setStats(statsRes.data);
      setReferrals(listRes.data.referrals || []);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    if (!stats?.referral_code) return '';
    return `${window.location.origin}/auth?ref=${stats.referral_code}`;
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${type} copied to clipboard!`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareReferral = async () => {
    const link = getReferralLink();
    const text = `Join this awesome trading bot platform and get bonus credits! Use my referral link: ${link}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Trading Bot Platform',
          text: text,
          url: link
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(link, 'Link');
        }
      }
    } else {
      copyToClipboard(link, 'Link');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/account">
            <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="back-btn">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Gift className="h-6 w-6 text-green-400" />
              Referral Program
            </h1>
            <p className="text-blue-200 text-sm">Earn credits by inviting friends</p>
          </div>
        </div>

        {/* How it Works */}
        <Alert className="mb-8 bg-gradient-to-r from-green-500/20 to-blue-500/20 border-green-500/50 text-green-100">
          <Gift className="h-4 w-4" />
          <AlertDescription>
            <strong>How it works:</strong> Share your referral link. When a friend signs up and makes their first purchase, 
            you earn <span className="font-bold text-green-300">50 credits</span> and they get an extra <span className="font-bold text-green-300">25 credits</span> bonus!
          </AlertDescription>
        </Alert>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : (
          <>
            {/* Your Referral Link */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm mb-8" data-testid="referral-link-card">
              <CardHeader>
                <CardTitle className="text-white">Your Referral Link</CardTitle>
                <CardDescription className="text-blue-200">Share this link with friends</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={getReferralLink()}
                    readOnly
                    className="bg-white/5 border-white/20 text-white font-mono text-sm"
                    data-testid="referral-link-input"
                  />
                  <Button
                    onClick={() => copyToClipboard(getReferralLink(), 'Link')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="copy-link-btn"
                  >
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg">
                    <span className="text-blue-200 text-sm">Your Code:</span>
                    <span className="text-white font-mono font-bold" data-testid="referral-code">{stats?.referral_code}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(stats?.referral_code, 'Code')}
                      className="text-white hover:bg-white/10 h-6 px-2"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button
                    onClick={shareReferral}
                    variant="outline"
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    data-testid="share-btn"
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-white/10 border-white/20" data-testid="stat-total">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
                  <p className="text-blue-200 text-sm">Total Referrals</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20" data-testid="stat-successful">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <p className="text-3xl font-bold text-white">{stats?.successful_referrals || 0}</p>
                  <p className="text-blue-200 text-sm">Converted</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20" data-testid="stat-pending">
                <CardContent className="p-6 text-center">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-yellow-400" />
                  <p className="text-3xl font-bold text-white">{stats?.pending_referrals || 0}</p>
                  <p className="text-blue-200 text-sm">Pending</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20" data-testid="stat-earned">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <p className="text-3xl font-bold text-white">{stats?.total_credits_earned || 0}</p>
                  <p className="text-blue-200 text-sm">Credits Earned</p>
                </CardContent>
              </Card>
            </div>

            {/* Referral List */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="referral-list-card">
              <CardHeader>
                <CardTitle className="text-white">Your Referrals</CardTitle>
                <CardDescription className="text-blue-200">People who signed up with your code</CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto mb-4 text-blue-400/50" />
                    <p className="text-blue-200">No referrals yet</p>
                    <p className="text-blue-300 text-sm mt-2">Share your link to start earning!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref, index) => (
                      <div 
                        key={ref.id || index} 
                        className="flex items-center justify-between p-4 bg-white/5 rounded-lg"
                        data-testid={`referral-item-${index}`}
                      >
                        <div>
                          <p className="text-white font-medium">{ref.referee_email}</p>
                          <p className="text-blue-300 text-sm">Joined {formatDate(ref.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {ref.status === 'converted' ? (
                            <>
                              <Badge className="bg-green-500">Converted</Badge>
                              <span className="text-green-400 font-semibold">+{ref.referrer_bonus} credits</span>
                            </>
                          ) : (
                            <Badge className="bg-yellow-500/80">Pending Purchase</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
